import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useHeroAward, useSetHeroAward } from '@/api/hooks'
import { useActorPermissions } from '@/hooks/useActorPermissions'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { getCategoryBadgeClass, getCategoryRowClass } from '@/lib/classColors'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { HeroAwardStateOutput, TShow } from '@schemas'

export function openHeroReport(show: Pick<TShow, 'id' | 'name'>) {
  const url = new URL('/print/reports/hero-award', window.location.origin)
  url.searchParams.set('showId', show.id!)
  url.searchParams.set('showName', show.name)
  window.open(url.toString(), '_blank', 'noopener,noreferrer')
}

export function HeroShortlist({
  state,
  stale = false,
  showEntrantNames = false,
  currentSlug,
}: {
  state: HeroAwardStateOutput
  stale?: boolean
  showEntrantNames?: boolean
  currentSlug?: string | null
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">
          {stale ? 'Status unconfirmed' : state.ready ? 'Ready' : 'Tentative'}
        </Badge>
        <span>
          {state.placement
            ? `${state.placement} place · ${state.candidates.length} candidate${state.candidates.length === 1 ? '' : 's'}`
            : 'No qualifying placement · 0 candidates'}
        </span>
      </div>
      {stale ? (
        <p role="alert" className="text-sm text-destructive">
          Updates are unavailable. This list may be out of date; readiness cannot be confirmed until
          the connection recovers.
        </p>
      ) : (
        !state.ready && (
          <p className="text-sm text-muted-foreground">
            {state.unfinishedClasses}{' '}
            {state.unfinishedClasses === 1 ? 'class remains' : 'classes remain'} unfinished. Further
            results or eligibility changes may change this list.
          </p>
        )
      )}
      {state.candidates.length ? (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b">
              <th className="py-2 pr-4">Category ID</th>
              <th className="py-2">Exhibit ID</th>
              {showEntrantNames && <th className="py-2 pl-4">Entrant name</th>}
            </tr>
          </thead>
          <tbody>
            {state.candidates.map(candidate => {
              const divisionCode = candidate.categoryId.charAt(0).toUpperCase()
              const isWinner = candidate.exhibitId === state.winner?.exhibitId
              return (
                <tr
                  key={candidate.exhibitId}
                  className={cn(
                    'border-b',
                    isWinner && 'font-medium',
                    isWinner && (getCategoryRowClass(divisionCode) || 'bg-muted/50')
                  )}
                >
                  <td className="py-2 pr-4">
                    {candidate.categoryId}
                    {isWinner && (
                      <>
                        {' '}
                        <Badge
                          variant="outline"
                          className={cn('ml-2 align-middle', getCategoryBadgeClass(divisionCode))}
                        >
                          Winner
                        </Badge>
                      </>
                    )}
                  </td>
                  <td className="py-2 font-mono">{candidate.entryNumber}</td>
                  {showEntrantNames && (
                    <td className="py-2 pl-4">
                      {candidate.entryId && candidate.entrantName ? (
                        <Link
                          to={showAware(`/entries/${candidate.entryId}`, { currentSlug })}
                          className="text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/50"
                        >
                          {candidate.entrantName}
                        </Link>
                      ) : (
                        (candidate.entrantName ?? '—')
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-sm">
          {state.ready
            ? 'All award-bearing classes are finalized, but no eligible entrant has a qualifying first- through sixth-place entry. The Hero Award remains unset.'
            : 'No eligible placed entries are available yet. Staff must confirm entrant eligibility and record class results.'}
        </p>
      )}
    </div>
  )
}

export function HeroWinner({ state }: { state?: HeroAwardStateOutput }) {
  if (!state?.enabled || !state.winner) return null
  return (
    <div className="space-y-1">
      <h3 className="font-semibold">Hero Award</h3>
      <p>
        Category {state.winner.categoryId} · Exhibit ID{' '}
        <span className="font-mono">{state.winner.entryNumber}</span>
      </p>
    </div>
  )
}

export function HeroAwardCard({ show, selection = false }: { show: TShow; selection?: boolean }) {
  if (show.awardsPolicy?.heroAwardEnabled !== true) return null
  return <EnabledHeroAwardCard show={show} selection={selection} />
}

function EnabledHeroAwardCard({ show, selection }: { show: TShow; selection: boolean }) {
  const enabled = true
  const permissions = useActorPermissions(show.id)
  const canRead = permissions.can('reports:results')
  const query = useHeroAward(show.id, enabled && canRead)
  const save = useSetHeroAward()
  const [nameVisibility, setNameVisibility] = useState({ showId: show.id, visible: false })
  const showEntrantNames = nameVisibility.showId === show.id && nameVisibility.visible
  const [draft, setDraft] = useState<{
    showId: string
    savedWinnerId: string
    exhibitId: string
  } | null>(null)
  const currentSlug = useShowSlug()
  const location = useLocation()
  const cardRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (selection && location.hash === '#hero-award')
      cardRef.current?.scrollIntoView?.({ block: 'start' })
  }, [selection, location.hash, show.id, canRead])
  const state = query.data
  const savedWinnerId = state?.winner?.exhibitId ?? ''
  if (!enabled || !canRead) return null
  const currentDraft =
    draft?.showId === show.id && draft?.savedWinnerId === savedWinnerId ? draft : null
  const selected = currentDraft?.exhibitId ?? savedWinnerId
  const stale = query.isError || query.isPaused
  const canSave =
    permissions.can('awards:finalize') && ['judging', 'finalized'].includes(show.status)
  return (
    <Card id="hero-award" ref={cardRef} className="scroll-mt-4">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle>Hero Award</CardTitle>
        <Button
          type="button"
          variant={showEntrantNames ? 'secondary' : 'outline'}
          onClick={() =>
            setNameVisibility(previous => ({
              showId: show.id,
              visible: previous.showId === show.id ? !previous.visible : true,
            }))
          }
          aria-pressed={showEntrantNames}
        >
          {showEntrantNames ? 'Hide entrant names' : 'Show entrant names'}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Judges choose from eligible veterans’ and first responders’ highest-placed exhibits,
          starting with first place.
        </p>
        {state ? (
          <>
            <HeroShortlist
              state={state}
              stale={stale}
              showEntrantNames={showEntrantNames}
              currentSlug={currentSlug}
            />
            <HeroWinner state={state} />
            {state.reselectionRequired && (
              <p role="status" className="text-sm">
                The previous winner is no longer eligible. Select a new winner when the list is
                ready.
              </p>
            )}
            {selection && canSave && (
              <div className="space-y-2">
                {state.winner && !currentDraft ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      disabled={!state.ready || stale || save.isPending}
                      onClick={() => {
                        setDraft({
                          showId: show.id!,
                          savedWinnerId,
                          exhibitId: savedWinnerId,
                        })
                      }}
                    >
                      Change Hero Award
                    </Button>
                    <Button
                      variant="outline"
                      disabled={stale || save.isPending}
                      onClick={() => save.mutate({ showId: show.id!, exhibitId: null })}
                    >
                      Clear Hero Award
                    </Button>
                  </div>
                ) : (
                  <>
                    <label className="block text-sm font-medium" htmlFor="hero-winner">
                      Winning exhibit
                    </label>
                    <select
                      id="hero-winner"
                      className="w-full rounded-md border bg-background p-2 text-foreground"
                      value={selected}
                      onChange={event =>
                        setDraft({
                          showId: show.id!,
                          savedWinnerId,
                          exhibitId: event.target.value,
                        })
                      }
                      disabled={!state.ready || stale || save.isPending}
                    >
                      <option value="">Choose an eligible exhibit</option>
                      {state.candidates.map(candidate => (
                        <option key={candidate.exhibitId} value={candidate.exhibitId}>
                          {candidate.categoryId} — {candidate.entryNumber}
                        </option>
                      ))}
                    </select>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        disabled={
                          !state.ready ||
                          stale ||
                          save.isPending ||
                          selected === savedWinnerId ||
                          !state.candidates.some(candidate => candidate.exhibitId === selected)
                        }
                        onClick={() => save.mutate({ showId: show.id!, exhibitId: selected })}
                      >
                        Save Hero Award
                      </Button>
                      {state.winner && (
                        <Button variant="outline" onClick={() => setDraft(null)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </>
                )}
                {save.isError && (
                  <p role="alert" className="text-sm text-destructive">
                    {save.error.message}
                  </p>
                )}
              </div>
            )}
          </>
        ) : (
          <p role={query.isError ? 'alert' : 'status'}>
            {query.isError
              ? 'Unable to load the Hero Award shortlist. Retrying automatically.'
              : 'Loading Hero Award shortlist…'}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openHeroReport(show)}>
            Print eligibility report
          </Button>
          {!selection && (
            <Button asChild variant="outline">
              <Link to={`${showAware('/awards', { currentSlug })}#hero-award`}>
                Open Hero Award
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
